import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { en } from "./en";
import { sr } from "./sr";
import { LOCALES, LANGS, isLang, resolveLang, type Lang } from "../../../../shared/locales";
import { translateCatalog } from "../../../../shared/catalog";
import { CATALOGS, hasNewCatalog } from "./catalogs";

export { LANGS, LOCALES };
export type { Lang };

/**
 * The language layer (4b step 3b). The member picks a language for this browser (localStorage
 * "vrelis-lang", default English); <html lang>, every text and every date follow it. Components
 * read texts with useT(); code outside React (errors, the archive README, dates) uses msg().
 */
export type Messages = typeof en;
export const DICTIONARIES = Object.fromEntries(LANGS.map((lang) => {
  const meta = LOCALES[lang];
  const texts = lang === "sr" ? sr : hasNewCatalog(lang) ? translateCatalog(en, CATALOGS[lang], meta.locale) : en;
  if (hasNewCatalog(lang)) {
    const onePerson = CATALOGS[lang]["compose.oneMember"];
    texts.compose.oneMember = (name) => name ?? onePerson;
  }
  return [lang, { ...texts, htmlLang: meta.htmlLang, locale: meta.locale, langName: meta.name }];
})) as Record<Lang, Messages>;
/** Each language named in its own writing system. */
export const langName = (l: Lang): string => LOCALES[l].name;
const KEY = "vrelis-lang";

function stored(): Lang {
  let saved: unknown;
  try {
    saved = localStorage.getItem(KEY);
  } catch { /* Storage may be disabled. */ }
  if (isLang(saved)) return saved;
  // A translated mail can introduce its language on a browser with no saved preference.
  try { return resolveLang(new URLSearchParams(location.search).get("lang")) ?? "en"; }
  catch { return "en"; }
}

let current: Lang = stored();

/** The current language's texts, for code outside React. Components use useT() so they re-render on a change. */
export const msg = (): Messages => DICTIONARIES[current];

type I18n = { lang: Lang; t: Messages; setLang: (lang: Lang) => void };
const I18nContext = createContext<I18n>({ lang: current, t: DICTIONARIES[current], setLang: () => {} });

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setState] = useState<Lang>(current);
  useEffect(() => {
    document.documentElement.lang = DICTIONARIES[lang].htmlLang;
    document.documentElement.dir = LOCALES[lang].dir;
    document.title = DICTIONARIES[lang].title;
    try { localStorage.setItem(KEY, lang); } catch { /* Storage blocked. */ }
  }, [lang]);
  const setLang = (next: Lang) => {
    if (!isLang(next)) return;
    current = next;
    try {
      localStorage.setItem(KEY, next);
    } catch {
      // Storage blocked: the choice lasts until the page is closed.
    }
    setState(next);
  };
  return <I18nContext.Provider value={{ lang, t: DICTIONARIES[lang], setLang }}>{children}</I18nContext.Provider>;
}

export const useT = (): Messages => useContext(I18nContext).t;
export const useLang = (): I18n => useContext(I18nContext);

/** An error as the member should read it: a known API code in their language, else the message. */
export function errorText(err: unknown): string {
  const t = msg();
  const e = err as { code?: unknown; message?: unknown } | null;
  if (e && typeof e.code === "string" && e.code in t.apiErrors) {
    const entry = t.apiErrors[e.code];
    return typeof entry === "function" ? entry(String(e.message ?? "")) : entry;
  }
  return err instanceof Error ? err.message : String(err);
}
